/* eslint-disable react-hooks/exhaustive-deps */
import React, { Children, useEffect, useState } from 'react';
import { WALLETS_AND_GROUPS_PATHS } from 'constants/wallets-and-groups-paths';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { possiblyNavigateToMetadataSearch } from '../../../../lib';
import {
    typeData,
    createAndUpdateSystemWalletDefaults,
    CreateAndUpdateSystemWalletFormData,
    CreateAndUpdateSystemWalletSchema,
    updateSystemWalletContainerStyles,
    ReadOnlyFields,
} from './create-or-edit-system-wallet-helper';
import {
    useDeleteSystemWallet,
    useSystemWalletDetails,
    useUpdateSystemWallet,
    useUserGroupList,
} from 'hooks';
import { Grid, MenuItem, Typography } from '@mui/material';
import { styles as confirmationModalStyles } from 'components/confirmation-modal/confirmation-modal-helper';
import { useAuth, usePermission } from '../../../../core';
import { LabelAndValueComponent } from '../create-or-edit-user-wallet/user-wallet-from-helper';
import { Select } from '@biq/apex-ui';
import {
    getUserGroupIdName,
    SYSTEM_WALLETS,
    walletsAndGroupsCommonStyles,
} from 'pages/wallets-and-groups/wallets-and-groups-helper';
import { SystemWalletTypeDialog } from './SystemWalletTypeDialog';
import { AutocompleteUserGroupField } from './AutocompleteUserGroupField';
import { DescriptionTextField } from '../description-field/DescriptionTextField';
import { UpdateWalletsAndGroupsWrapper } from '../update-wallets-and-groups-wrapper/UpdateWalletsAndGroupsWrapper';

export const UpdateSystemWallet = (): React.JSX.Element => {
    const { getToken } = useAuth();
    const { hasUserCurrentRole } = usePermission();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [systemWalletType, setSystemWalletType] = useState<string>('');
    const [openChangeTypeDialog, setOpenChangeTypeDialog] =
        useState<boolean>(false);
    const [isChangesUnsaved, setIsChangesUnsaved] = useState<boolean>(false);
    const [openCancelModal, setOpenCancelModal] = useState<boolean>(false);
    const [filteredTypeData, setFilteredTypeData] = useState<
        Array<{
            name: string;
            value: string;
        }>
    >(typeData);
    const [isWriteUserGroupFieldEnabled, setIsWriteUserGroupFieldEnabled] =
        useState<boolean>(false);
    const [readOnly, setReadOnly] = useState<boolean>(false);
    const [optionsData, setOptionsData] = useState<any[]>([]);
    const {
        handleSubmit,
        control,
        setValue,
        getValues,
        watch,
        formState: { errors },
    } = useForm<CreateAndUpdateSystemWalletFormData>({
        mode: 'all',
        resolver: yupResolver(CreateAndUpdateSystemWalletSchema),
        defaultValues: createAndUpdateSystemWalletDefaults,
    });
    const { id } = useParams();
    const { data: systemWalletDetails, isLoading: systemWalletDetailsLoading } =
        useSystemWalletDetails({
            id: id,
        });
    const { mutate: updateSystemWallet } = useUpdateSystemWallet();
    const { mutate: deleteSystemWallet } = useDeleteSystemWallet();
    const { data: userGroupsData, isLoading: userGroupsLoading } =
        useUserGroupList();

    useEffect(() => {
        if (hasUserCurrentRole('sdzGovernance')) {
            setReadOnly(false);
        } else {
            setReadOnly(true);
        }
    }, []);

    useEffect(() => {
        if (!userGroupsLoading && userGroupsData) {
            let userGroupsDataArr = [];
            userGroupsDataArr = userGroupsData?.data.map((userGroup) => ({
                name: userGroup.name,
                id: userGroup.id,
            }));
            userGroupsDataArr.sort((a: any, b: any) => {
                return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
            });
            setOptionsData(userGroupsDataArr);
        }
        if (!systemWalletDetailsLoading && !userGroupsLoading) {
            let filteredType = [];
            const accessControlUserGroupName = getUserGroupIdName(
                userGroupsData?.data ?? [],
                systemWalletDetails?.data?.accessControlUserGroup
            );
            const writeUserGroupName = getUserGroupIdName(
                userGroupsData?.data ?? [],
                systemWalletDetails?.data?.writeUserGroup
            );
            if (
                systemWalletDetails?.data?.type === 'TENANT_OWNER' ||
                systemWalletDetails?.data?.type === 'TENANT_LIMITED_OWNER'
            ) {
                filteredType = filteredTypeData.filter(
                    (item) =>
                        item.value === 'TENANT_OWNER' ||
                        item.value === 'TENANT_LIMITED_OWNER'
                );
                setFilteredTypeData(filteredType);
                setIsWriteUserGroupFieldEnabled(true);
                setValue('writeUserGroup', {
                    name: writeUserGroupName,
                    id: systemWalletDetails?.data?.writeUserGroup,
                });
            } else {
                filteredType = filteredTypeData.filter(
                    (item) =>
                        item.value === 'CUSTODIAN_OWNER' ||
                        item.value === 'CUSTODIAN_LIMITED_OWNER'
                );
                setFilteredTypeData(filteredType);
                setIsWriteUserGroupFieldEnabled(false);
                setValue('writeUserGroup', {
                    name: '',
                    id: '',
                });
            }
            setValue('type', systemWalletDetails?.data?.type);
            setValue('description', systemWalletDetails?.data?.description);
            setValue('accessControlUserGroup', {
                name: accessControlUserGroupName,
                id: systemWalletDetails?.data?.accessControlUserGroup,
            });
        }
    }, [systemWalletDetailsLoading, userGroupsLoading]);

    const handleSystemWalletsNavigation = (): void => {
        possiblyNavigateToMetadataSearch(
            navigate,
            `${WALLETS_AND_GROUPS_PATHS.WALLETS_AND_GROUPS_PATH}?option=${SYSTEM_WALLETS}`,
            searchParams
        );
    };

    const onSubmit = (data: CreateAndUpdateSystemWalletFormData): void => {
        setIsChangesUnsaved(false);
        updateSystemWallet(
            {
                token: getToken(),
                description: data.description,
                type: data.type,
                writeUserGroup: data.writeUserGroup.id,
                accessControlUserGroup: data.accessControlUserGroup.id,
                id: id as string,
            },
            {
                onSuccess: () => {
                    setIsLoading(false);
                    setTimeout(() => {
                        handleSystemWalletsNavigation();
                    }, 500);
                },
                onError: () => {
                    setIsLoading(false);
                    setIsChangesUnsaved(true);
                },
            }
        );
    };

    const handleDeleteButton = (): void => {
        setTimeout(() => {
            setIsChangesUnsaved(false);
            handleSystemWalletsNavigation();
        }, 500);
        deleteSystemWallet({
            token: getToken(),
            id: id as string,
        });
    };

    const handleCancelButton = (): void => {
        if (isChangesUnsaved) {
            setOpenCancelModal(true);
        } else {
            handleSystemWalletsNavigation();
        }
    };

    const handleCancelModalConfirmation = (): void => {
        setOpenCancelModal(false);
        handleSystemWalletsNavigation();
    };

    const handleCancelModal = (): void => {
        setOpenCancelModal(false);
    };

    const handleCloseDialog = (): void => {
        setOpenChangeTypeDialog(false);
    };

    const handleChangeType = (value: any): void => {
        setOpenChangeTypeDialog(true);
        setIsChangesUnsaved(true);
        setSystemWalletType(value);
    };

    const handleConfirmChangeType = (): void => {
        setValue('type', systemWalletType);
        setValue('accessControlUserGroup', {
            name: '',
            id: '',
        });
        setValue('writeUserGroup', {
            name: '',
            id: '',
        });
        setOpenChangeTypeDialog(false);
    };

    const combinedLoading = systemWalletDetailsLoading || userGroupsLoading;

    return (
        <UpdateWalletsAndGroupsWrapper
            title={readOnly ? 'View System Wallet' : 'Edit System Wallet'}
            readOnly={readOnly}
            combinedLoading={combinedLoading}
            breadCrumData={'System Wallet'}
            handleCancelButton={handleCancelButton}
            handleCancelModal={handleCancelModal}
            onBackArrowClick={handleCancelButton}
            deleteMenuItemText={'Delete Wallet'}
            headingText={'Wallet Info'}
            onSaveButtonClick={handleSubmit(onSubmit)}
            handleCancelModalConfirmation={handleCancelModalConfirmation}
            isLoading={isLoading}
            isChangesUnsaved={isChangesUnsaved}
            openCancelModal={openCancelModal}
            unsavedChangesConfirmationText={
                'This system wallet has unsaved changes. Are you sure you want to close without saving? You will lose all changes.'
            }
            confirmationKey={id?.slice(-6) as string}
            confirmationButtonText={'Delete System Wallet'}
            confirmationHeadingText={'Confirm System Wallet Deletion'}
            confirmationInputLabelText={`Type ${id?.slice(-6)} to confirm`}
            handleConfirmation={handleDeleteButton}
            confirmationSubHeadingText={
                <Typography>
                    By typing{' '}
                    <Typography
                        component='span'
                        sx={confirmationModalStyles.textBoldStyles}
                    >
                        ‘{id?.slice(-6)}’
                    </Typography>{' '}
                    below, you understand that you are permanently deleting this
                    system wallet. This action{' '}
                    <Typography
                        component='span'
                        sx={confirmationModalStyles.textBoldStyles}
                    >
                        CANNOT
                    </Typography>{' '}
                    be undone.
                </Typography>
            }
        >
            <Grid sx={updateSystemWalletContainerStyles(readOnly)}>
                <LabelAndValueComponent label={'ID'} value={id as string} />
                {readOnly ? (
                    <ReadOnlyFields
                        getValues={getValues}
                        isWriteUserGroupFieldEnabled={
                            isWriteUserGroupFieldEnabled
                        }
                    />
                ) : (
                    <>
                        <Grid sx={walletsAndGroupsCommonStyles.textFieldStyles}>
                            <Controller
                                name='type'
                                control={control}
                                render={({ field }): React.JSX.Element => (
                                    <Select
                                        {...field}
                                        variant='outlined'
                                        label={'Type'}
                                        required
                                        onChange={(event): void => {
                                            handleChangeType(
                                                event.target.value
                                            );
                                        }}
                                        error={!!errors.type}
                                        errorText={
                                            errors.type
                                                ? errors.type.message
                                                : ''
                                        }
                                    >
                                        {Children.toArray(
                                            filteredTypeData.map((type) => (
                                                <MenuItem value={type.value}>
                                                    {type.name}
                                                </MenuItem>
                                            ))
                                        )}
                                    </Select>
                                )}
                            />
                        </Grid>
                        <Grid sx={walletsAndGroupsCommonStyles.gridStyles}>
                            <AutocompleteUserGroupField
                                name='accessControlUserGroup'
                                control={control}
                                label='Access Control User Group'
                                error={errors.accessControlUserGroup}
                                fieldValue={watch('accessControlUserGroup')}
                                options={optionsData}
                                onChange={(value): void => {
                                    setValue('accessControlUserGroup', value);
                                }}
                                handleUnsavedChanges={(): void => {
                                    setIsChangesUnsaved(true);
                                }}
                            />
                        </Grid>
                        {isWriteUserGroupFieldEnabled ? (
                            <Grid sx={walletsAndGroupsCommonStyles.gridStyles}>
                                <AutocompleteUserGroupField
                                    name='writeUserGroup'
                                    control={control}
                                    label='Write User Group'
                                    error={errors.writeUserGroup}
                                    fieldValue={watch('writeUserGroup')}
                                    options={optionsData}
                                    onChange={(value): void => {
                                        setValue('writeUserGroup', value);
                                    }}
                                    handleUnsavedChanges={(): void => {
                                        setIsChangesUnsaved(true);
                                    }}
                                />
                            </Grid>
                        ) : null}
                        <DescriptionTextField
                            control={control}
                            rows={5}
                            error={errors.description}
                            required={true}
                            onChange={(): void => {
                                setIsChangesUnsaved(true);
                            }}
                        />
                    </>
                )}
            </Grid>
            {openChangeTypeDialog && (
                <SystemWalletTypeDialog
                    openDialog={openChangeTypeDialog}
                    handleCloseDialog={handleCloseDialog}
                    handleChangeType={handleConfirmChangeType}
                />
            )}
        </UpdateWalletsAndGroupsWrapper>
    );
};
